/** UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — inventory data-grid + V2 comparison cards. */
import { useState } from 'react';
import {
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2Checkbox,
  V2DataGrid,
  V2EmptyState,
  V2Modal,
  V2Tabs,
} from '@sakhtar/design-system/react';
import type { GuidedImportPack } from '../../lib/dataImport/guidedImportTemplate.js';
import {
  relationConfidence,
  type ReviewRow,
  type ReviewRelation,
} from '../../lib/dataImport/guidedImportReview.js';
import { buildRelationReviewGroups } from '../../lib/dataImport/relationReviewGroups.js';
import type { ImportAnalysis } from '../../lib/dataImport/guidedImportAnalysis.js';
import { importAnalysisPresentation } from '../../lib/dataImport/guidedImportAnalysis.js';
import './guidedRelationReview.css';

export interface RelationDecisionRequest {
  readonly proposalId: string;
  readonly decision: 'link' | 'separate';
  readonly canonicalRowId?: string;
}
export interface RelationSaveProgress {
  readonly done: number;
  readonly total: number;
}
interface Props {
  readonly rows: readonly ReviewRow[];
  readonly proposals: readonly (ReviewRelation & { readonly reason?: string })[];
  readonly packs: readonly GuidedImportPack[];
  readonly files?: readonly { readonly packId: string; readonly fileName: string }[] | undefined;
  readonly activePack: string;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly error: string | null;
  readonly saveProgress: RelationSaveProgress | null;
  readonly analysisRunning: boolean;
  readonly analysis: ImportAnalysis | null | undefined;
  readonly analysisNotice: string | null;
  readonly hasPreview: boolean;
  readonly onAnalyze: () => void;
  readonly onStop: () => void;
  readonly onReviewPair: (id: string) => void;
  readonly onDecide: (items: readonly RelationDecisionRequest[]) => Promise<boolean>;
  readonly onNext: () => void;
}
const nf = (n: number) => n.toLocaleString('fa-IR');

export function GuidedRelationReview(props: Props) {
  const { rows, proposals, packs, busy, disabled } = props;
  const [tab, setTab] = useState(() => {
    const initial = buildRelationReviewGroups(props.rows, props.proposals).filter(
      (g) => g.pendingCount > 0,
    );
    return initial.some((g) => g.category === 'repeated')
      ? 'repeated'
      : initial.some((g) => g.category === 'conflict')
        ? 'conflict'
        : initial.length
          ? 'suggested'
          : 'history';
  });
  const [search, setSearch] = useState('');
  const [groupId, setGroupId] = useState<string | null>(null);
  const [canonical, setCanonical] = useState('');
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [confirmSeparate, setConfirmSeparate] = useState<readonly string[] | null>(null);
  const groups = buildRelationReviewGroups(rows, proposals);
  const rowById = new Map(rows.map((r) => [r.rowId, r]));
  const byId = new Map(proposals.map((p) => [p.proposalId, p]));
  const scope = groups.filter(
    (g) =>
      props.activePack === 'all' ||
      g.rowIds.some((id) => rowById.get(id)?.packId === props.activePack),
  );
  const pending = scope.filter((g) => g.pendingCount > 0);
  const repeated = pending.filter((g) => g.category === 'repeated');
  const conflicts = pending.filter((g) => g.category === 'conflict');
  const suggestions = pending.filter((g) => g.category === 'suggested');
  const history = scope.filter((g) => g.pendingCount === 0);
  const currentGroup = groups.find((g) => g.id === groupId);
  const members =
    currentGroup?.rowIds.flatMap((id) => (rowById.get(id) ? [rowById.get(id)!] : [])) ?? [];
  const groupPending =
    currentGroup?.pendingProposalIds.flatMap((id) => (byId.get(id) ? [byId.get(id)!] : [])) ?? [];
  const status = importAnalysisPresentation(
    { status: 'review', proposals, analysis: props.analysis ?? null },
    props.hasPreview,
  );
  const globallyPending = proposals.some((p) => p.decision === 'pending');
  const category =
    tab === 'repeated'
      ? repeated
      : tab === 'suggested'
        ? suggestions
        : tab === 'conflict'
          ? conflicts
          : history;
  const displayed = category.filter(
    (g) =>
      !search ||
      [g.name, g.code ?? '', ...g.rowIds.map((id) => source(rowById.get(id)))]
        .join(' ')
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase()),
  );
  const selectable = displayed
    .flatMap((g) => g.pendingProposalIds)
    .filter((id) => byId.get(id)?.source === 'smart');
  const picked = selected.filter((id) => selectable.includes(id));
  const separation = (confirmSeparate ?? []).flatMap((id) => {
    const proposal = byId.get(id);
    return proposal?.decision === 'pending' ? [proposal] : [];
  });
  function source(row: ReviewRow | undefined) {
    if (!row) return 'ردیف منبع در دسترس نیست';
    const pack = packs.find((p) => p.id === row.packId);
    const sheet = pack?.sheets.find((s) => s.id === row.sheetId);
    return `${pack?.label ?? row.packId}، ${sheet?.name ?? row.sheetId}، ردیف ${nf(row.sourceRow)}`;
  }
  function name(row: ReviewRow | undefined) {
    return String(row?.values['name'] || row?.values['code'] || 'ردیف منبع');
  }
  function reviewGroup(id: string) {
    setCanonical('');
    setGroupId(id);
  }
  function pair(id: string) {
    setGroupId(null);
    props.onReviewPair(id);
  }
  async function linkGroup() {
    if (
      !currentGroup?.canBulkLink ||
      !canonical ||
      !members.some((r) => r.rowId === canonical) ||
      busy ||
      disabled
    )
      return;
    const done = await props.onDecide(
      groupPending.map((p) => ({
        proposalId: p.proposalId,
        decision: 'link',
        canonicalRowId: canonical,
      })),
    );
    if (done) {
      setGroupId(null);
      if (repeated.length <= 1)
        setTab(conflicts.length ? 'conflict' : suggestions.length ? 'suggested' : 'history');
    }
  }
  async function separateSelected() {
    if (!separation.length || busy || disabled) return;
    const done = await props.onDecide(
      separation.map((p) => ({ proposalId: p.proposalId, decision: 'separate' })),
    );
    if (done) {
      setSelected((ids) => ids.filter((id) => !confirmSeparate?.includes(id)));
      setConfirmSeparate(null);
      setGroupId(null);
    }
  }
  const progress = props.saveProgress ? (
    <div className="relation-review__progress" role="status">
      <span>
        {nf(props.saveProgress.done)} از {nf(props.saveProgress.total)} تصمیم ذخیره شد.
      </span>
      {busy ? (
        <V2Button size="sm" onClick={props.onStop}>
          توقف پس از ذخیرهٔ فعلی
        </V2Button>
      ) : null}
    </div>
  ) : null;
  const actions = (g: ReturnType<typeof buildRelationReviewGroups>[number]) => {
    const proposal = byId.get(g.proposalIds[0]!);
    return (
      <div className="relation-review__actions" data-proposal-id={proposal?.proposalId}>
        {g.conflict ? (
          <V2Badge tone="danger">تعارض هویتی</V2Badge>
        ) : !g.pendingCount ? (
          <V2Badge tone="neutral">تصمیم ذخیره شده</V2Badge>
        ) : null}
        {tab === 'suggested' && proposal?.source === 'smart' && proposal.decision === 'pending' ? (
          <V2Checkbox
            checked={selected.includes(proposal.proposalId)}
            disabled={busy || disabled}
            aria-label={`انتخاب پیشنهاد ${g.name}`}
            onCheckedChange={(checked) =>
              setSelected((ids) =>
                checked
                  ? [...new Set([...ids, proposal.proposalId])]
                  : ids.filter((id) => id !== proposal.proposalId),
              )
            }
          />
        ) : null}
        <V2Button
          size="sm"
          disabled={busy}
          aria-label={`${g.code ? 'بررسی گروه' : 'بررسی پیشنهاد'} ${g.name}`}
          onClick={() => (g.code ? reviewGroup(g.id) : pair(g.proposalIds[0]!))}
        >
          {g.code ? 'بررسی گروه' : g.pendingCount ? 'مقایسه و تصمیم' : 'مشاهدهٔ تصمیم'}
        </V2Button>
      </div>
    );
  };
  return (
    <div className="relation-review">
      <V2Card>
        <V2CardHeader
          icon="users"
          title="رکوردهای تکراری را یک‌بار بررسی کنید"
          subtitle="منبع‌ها را مقایسه کنید؛ سپس مشخص کنید یک طرف‌حساب هستند یا باید جدا بمانند."
        />
        <div className="relation-review__body">
          <div className="relation-review__summary" aria-label="کارهای باقی‌مانده">
            <div>
              <b>{nf(repeated.length)}</b>
              <span>گروه هم‌کد</span>
              <small>چند منبع، یک بررسی</small>
            </div>
            <div>
              <b>{nf(suggestions.length)}</b>
              <span>پیشنهاد مبهم</span>
              <small>نیازمند تصمیم شما</small>
            </div>
            <div>
              <b>{nf(conflicts.length)}</b>
              <span>تعارض هویتی</span>
              <small>اتصال گروهی مجاز نیست</small>
            </div>
          </div>
          <p className="relation-review__hint">
            هم‌کد بودن فقط برای دسته‌بندی است؛ هیچ رکوردی بدون انتخاب و تأیید شما متصل نمی‌شود.
          </p>
          {props.analysisRunning ? (
            <V2Alert tone="info" title="بررسی هوشمند در حال انجام است">
              <V2Button size="sm" onClick={props.onStop}>
                توقف تحلیل
              </V2Button>
            </V2Alert>
          ) : status.continuable || status.retryable ? (
            <V2Alert
              tone={props.analysis?.status === 'unavailable' ? 'warn' : 'info'}
              title="بررسی هوشمند هنوز کامل نشده است"
            >
              <p>
                می‌توانید همین موارد را بررسی کنید یا تحلیل باقی‌مانده را با یک‌بار شروع ادامه دهید.
              </p>
              <V2Button disabled={busy || disabled} onClick={props.onAnalyze}>
                تکمیل بررسی هوشمند
              </V2Button>
            </V2Alert>
          ) : props.analysis?.status === 'unavailable' ? (
            <V2Alert tone="warn" title="ساختار یار در دسترس نیست">
              بررسی دستی منبع‌ها همچنان در دسترس است. وضعیت تحلیل را در جزئیات ببینید.
            </V2Alert>
          ) : null}
          {progress}
          <V2Tabs
            activeId={tab}
            onChange={(id) => {
              setTab(id);
              setSearch('');
              setSelected([]);
            }}
            items={[
              { id: 'repeated', label: `گروه‌های تکراری (${nf(repeated.length)})` },
              { id: 'suggested', label: `پیشنهادهای نیازمند تصمیم (${nf(suggestions.length)})` },
              { id: 'conflict', label: `تعارض‌ها (${nf(conflicts.length)})` },
              { id: 'history', label: `تصمیم‌های ذخیره‌شده (${nf(history.length)})` },
            ]}
          />
          {tab === 'suggested' && selectable.length ? (
            <div className="relation-review__bulk">
              <V2Checkbox
                label="انتخاب همهٔ پیشنهادهای این فهرست"
                checked={selectable.every((id) => selected.includes(id))}
                disabled={busy || disabled}
                onCheckedChange={(checked) => setSelected(checked ? selectable : [])}
              />
              <V2Button
                disabled={busy || disabled || !picked.length}
                onClick={() => setConfirmSeparate(picked)}
              >
                جدا نگه داشتن انتخاب‌شده‌ها ({nf(picked.length)})
              </V2Button>
              <small>
                فقط موارد انتخاب‌شده و پس از تأیید شما جدا می‌مانند؛ امتیاز مدل به‌تنهایی تصمیم
                نیست.
              </small>
            </div>
          ) : null}
          <section
            aria-label={
              tab === 'repeated'
                ? 'گروه‌های تکراری'
                : tab === 'suggested'
                  ? 'پیشنهادهای نیازمند تصمیم'
                  : tab === 'conflict'
                    ? 'تعارض‌های هویتی'
                    : 'تصمیم‌های ذخیره‌شده'
            }
          >
            <V2DataGrid
              title={
                tab === 'repeated'
                  ? 'یک گروه برای هر کد مشترک'
                  : tab === 'suggested'
                    ? 'پیشنهادهای مبهم'
                    : tab === 'conflict'
                      ? 'موارد دارای تعارض'
                      : 'تاریخچهٔ تصمیم‌ها'
              }
              icon="users"
              densitySwitch={false}
              pageSize={8}
              rowKey={(g) => g.id}
              rows={displayed}
              search={{
                value: search,
                onChange: setSearch,
                placeholder: 'نام، کد یا منبع را جست‌وجو کنید',
              }}
              columns={[
                {
                  key: 'party',
                  header: 'طرف‌حساب و منبع‌ها',
                  wrap: true,
                  render: (g) => (
                    <div className="relation-review__record">
                      <b>{name(rowById.get(g.rowIds[0]!))}</b>
                      {g.code ? <span className="ltr">{g.code}</span> : null}
                      {g.rowIds.slice(0, 4).map((id) => (
                        <small key={id}>
                          {`${name(rowById.get(id))} — ${source(rowById.get(id))}`}
                        </small>
                      ))}
                      {g.rowIds.length > 4 ? (
                        <small>
                          و {nf(g.rowIds.length - 4)} منبع دیگر؛ همه در بررسی گروه نمایش داده
                          می‌شوند.
                        </small>
                      ) : null}
                      {actions(g)}
                    </div>
                  ),
                },
              ]}
              emptyState={
                <V2EmptyState
                  compact
                  title={search ? 'موردی با این جست‌وجو پیدا نشد' : 'این بخش مورد بازی ندارد'}
                  description={
                    search
                      ? 'عبارت جست‌وجو را تغییر دهید.'
                      : pending.length
                        ? 'بخش‌های دیگر را برای ادامهٔ بررسی باز کنید.'
                        : 'می‌توانید مقصد داده‌ها را بررسی کنید.'
                  }
                />
              }
            />
          </section>
          <div className="relation-review__next">
            <span>
              {globallyPending
                ? 'پس از تعیین ارتباط‌ها، مقصد داده‌ها را بررسی می‌کنید.'
                : 'ارتباط بازی باقی نمانده؛ مرحلهٔ بعد بررسی مقصد داده‌هاست.'}
            </span>
            <V2Button variant="primary" disabled={busy || globallyPending} onClick={props.onNext}>
              ادامه به مقصد داده‌ها
            </V2Button>
          </div>
          <details className="relation-review__technical" aria-label="جزئیات تحلیل ساختار یار">
            <summary>جزئیات تحلیل ساختار یار</summary>
            <p>
              {status.title}؛ {status.description}
            </p>
            {props.analysis ? (
              <dl>
                <div>
                  <dt>مقایسهٔ نامزد</dt>
                  <dd>{nf(props.analysis.candidatePairs)}</dd>
                </div>
                <div>
                  <dt>بررسی‌شده</dt>
                  <dd>{nf(status.processedPairs ?? 0)}</dd>
                </div>
                <div>
                  <dt>پاسخ معتبر مدل</dt>
                  <dd>{nf(status.answeredPairs ?? 0)}</dd>
                </div>
                <div>
                  <dt>باقی‌مانده</dt>
                  <dd>{nf(status.remainingPairs ?? 0)}</dd>
                </div>
              </dl>
            ) : null}
            {props.analysisNotice ? <p>{props.analysisNotice}</p> : null}
            <p>
              این عددها تعداد مقایسه‌ها هستند؛ درصد صحت فایل یا تعداد اشخاص یکتا نیستند. امتیاز هر
              پیشنهاد در پنجرهٔ مقایسه دیده می‌شود.
            </p>
          </details>
        </div>
      </V2Card>
      <V2Modal
        open={!!currentGroup && !confirmSeparate}
        size="lg"
        title="این ردیف‌ها یک طرف‌حساب هستند؟"
        dismissible={!busy}
        onClose={() => setGroupId(null)}
        footer={
          <>
            <V2Button disabled={busy} onClick={() => setGroupId(null)}>
              بستن
            </V2Button>
            {groupPending.length ? (
              <V2Button
                disabled={busy || disabled}
                onClick={() => setConfirmSeparate(groupPending.map((p) => p.proposalId))}
              >
                این منبع‌ها جدا بمانند
              </V2Button>
            ) : null}
            {groupPending.length ? (
              <V2Button
                variant="primary"
                disabled={busy || disabled || !currentGroup?.canBulkLink || !canonical}
                onClick={() => void linkGroup()}
              >
                تأیید اتصال گروه
              </V2Button>
            ) : null}
          </>
        }
      >
        <div className="relation-review__dialog">
          {props.error ? <V2Alert tone="danger">{props.error}</V2Alert> : null}
          {progress}
          <p>
            هر {nf(members.length)} منبع را مقایسه کنید. برای اتصال، ردیفی را که اطلاعات اصلی آن
            استفاده شود انتخاب کنید؛ بقیهٔ منابع حفظ می‌شوند.
          </p>
          {currentGroup && !currentGroup.canBulkLink && currentGroup.pendingCount ? (
            <V2Alert tone="warn">
              این گروه تعارض، تصمیم قبلی یا ارتباط ناقص دارد. ارتباط‌های باز را تک‌به‌تک بررسی کنید.
            </V2Alert>
          ) : null}
          <div className="relation-review__sources">
            {members.map((row) => {
              const sheet = packs
                .find((p) => p.id === row.packId)
                ?.sheets.find((s) => s.id === row.sheetId);
              return (
                <V2Card key={row.rowId}>
                  <V2CardHeader title={name(row)} subtitle={source(row)} />
                  <div className="relation-review__source-body">
                    <small>{props.files?.find((f) => f.packId === row.packId)?.fileName}</small>
                    <dl>
                      {sheet?.columns.map((c) => (
                        <div key={c.key}>
                          <dt>{c.label}</dt>
                          <dd>{String(row.values[c.key] || 'خالی')}</dd>
                        </div>
                      ))}
                    </dl>
                    {currentGroup?.canBulkLink && groupPending.length ? (
                      <label className="relation-review__primary">
                        <input
                          type="radio"
                          name="group-canonical"
                          value={row.rowId}
                          disabled={busy || disabled || row.errors.length > 0}
                          checked={canonical === row.rowId}
                          aria-label={`اطلاعات اصلی: ${source(row)}`}
                          onChange={() => setCanonical(row.rowId)}
                        />
                        اطلاعات اصلی از این منبع
                      </label>
                    ) : null}
                  </div>
                </V2Card>
              );
            })}
          </div>
          <V2Alert tone="info">
            فیلد خالی منبع اصلی از منبع دیگر پر نمی‌شود. انتخاب شما فقط ارتباط این منابع را مشخص
            می‌کند و هنوز ثبت نهایی داده‌ها نیست.
          </V2Alert>
          {groupPending.map((p) => (
            <V2Button
              key={p.proposalId}
              size="sm"
              disabled={busy}
              onClick={() => pair(p.proposalId)}
            >
              بررسی تک‌به‌تک: {source(rowById.get(p.leftRowId))} و{' '}
              {source(rowById.get(p.rightRowId))}
            </V2Button>
          ))}
        </div>
      </V2Modal>
      <V2Modal
        open={!!confirmSeparate}
        title="تأیید جدا ماندن منبع‌ها"
        dismissible={!busy}
        onClose={() => setConfirmSeparate(null)}
        footer={
          <>
            <V2Button disabled={busy} onClick={() => setConfirmSeparate(null)}>
              بازگشت به بررسی
            </V2Button>
            <V2Button
              variant="primary"
              disabled={busy || disabled || !separation.length}
              onClick={() => void separateSelected()}
            >
              تأیید جدا ماندن
            </V2Button>
          </>
        }
      >
        <div className="relation-review__dialog">
          {props.error ? <V2Alert tone="danger">{props.error}</V2Alert> : null}
          {progress}
          <p>
            {nf(separation.length)} ارتباط بازِ انتخاب‌شده جدا نگه داشته می‌شود. هیچ رکوردی حذف
            نمی‌شود و این کار ثبت نهایی اطلاعات نیست.
          </p>
          <p>منبع‌های زیر را بررسی کرده‌ام و تأیید می‌کنم این ارتباط‌ها ایجاد نشوند:</p>
          <ul className="relation-review__confirmation-list">
            {separation.map((p) => (
              <li key={p.proposalId}>
                <b>
                  {name(rowById.get(p.leftRowId))} / {name(rowById.get(p.rightRowId))}
                </b>
                <small>
                  {source(rowById.get(p.leftRowId))} / {source(rowById.get(p.rightRowId))}
                </small>
                {p.source === 'smart' ? (
                  <small>
                    احتمال یکسان بودن:{' '}
                    {relationConfidence(p).probability === null
                      ? 'گزارش نشده'
                      : nf(Math.round(relationConfidence(p).probability! * 100)) + '٪'}
                    ؛ تصمیم نهایی با شماست.
                  </small>
                ) : null}
              </li>
            ))}
          </ul>
          <small>
            اگر ذخیره در میانه متوقف شود، تصمیم‌های موفق حفظ می‌شوند؛ فقط موارد باقی‌مانده دوباره
            ارسال می‌شوند.
          </small>
        </div>
      </V2Modal>
    </div>
  );
}
